/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { accessAPI, formatFecha, Sidebar } from "../../shared";
import { Titulo } from "../../shared";
import { Boton } from "../../shared";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { Menu } from "primereact/menu";
import { Accordion, AccordionTab } from "primereact/accordion";
import { CampoInformacion } from "../../shared";

export default function AuditoriaSG() {
  const [loader, setLoader] = useState(true);
  const [auditoria, setAuditoria] = useState();
  const [procesosDisponibles, setProcesosDisponibles] = useState([]);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [loadingProcesos, setLoadingProcesos] = useState(false);
  const [agregandoProceso, setAgregandoProceso] = useState(null);
  const [eliminandoProceso, setEliminandoProceso] = useState(null);

  const toast = useRef(null);
  const menuRefs = useRef({});
  const { auditoriaId } = useParams();
  const navigate = useNavigate();

  // Cargar auditoría
  useEffect(() => {
    accessAPI(
      "GET",
      `auditorsg/auditoria/${auditoriaId}`,
      null,
      (respuesta) => {
        setAuditoria(respuesta);
        setLoader(false);
      },
      (respuesta) => {
        setLoader(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar la auditoría",
          sticky: true,
        });
      },
    );
  }, [auditoriaId]);

  // Cargar procesos disponibles cuando se abre el sidebar
  const abrirSidebarProcesos = () => {
    setSidebarVisible(true);
    setLoadingProcesos(true);
    accessAPI(
      "GET",
      `auditorsg/auditoria/${auditoriaId}/procesosDisponibles`,
      null,
      (respuesta) => {
        setProcesosDisponibles(respuesta);
        setLoadingProcesos(false);
      },
      (respuesta) => {
        setLoadingProcesos(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar procesos",
          life: 3000,
        });
      },
    );
  };

  // Agregar proceso a la auditoría
  const agregarProceso = (procesoId) => {
    setAgregandoProceso(procesoId);
    accessAPI(
      "POST",
      `auditorsg/auditoria/${auditoriaId}/proceso`,
      { procesoId },
      (respuesta) => {
        // Actualizar lista de procesos auditados
        setAuditoria((prev) => ({
          ...prev,
          procesosAuditados: [...prev.procesosAuditados, respuesta],
        }));
        // Remover de disponibles
        setProcesosDisponibles((prev) =>
          prev
            .map((grupo) => ({
              ...grupo,
              procesos: grupo.procesos.filter((p) => p.id !== procesoId),
            }))
            .filter((grupo) => grupo.procesos.length > 0),
        );
        setAgregandoProceso(null);
        toast.current.show({
          severity: "success",
          summary: "Proceso agregado",
          detail: "El proceso fue asignado a la auditoría",
          life: 3000,
        });
      },
      (respuesta) => {
        setAgregandoProceso(null);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al agregar proceso",
          life: 3000,
        });
      },
    );
  };

  // Seleccionar proceso al azar de un tipo de certificado
  const seleccionarAlAzar = (grupo) => {
    if (grupo.procesos.length === 0) return;
    const indiceAleatorio = Math.floor(Math.random() * grupo.procesos.length);
    const procesoAleatorio = grupo.procesos[indiceAleatorio];
    agregarProceso(procesoAleatorio.id);
  };

  // Eliminar proceso de la auditoría
  const eliminarProceso = (procesoAuditadoId) => {
    setEliminandoProceso(procesoAuditadoId);
    accessAPI(
      "DELETE",
      `auditorsg/auditoria/${auditoriaId}/proceso/${procesoAuditadoId}`,
      null,
      () => {
        setAuditoria((prev) => ({
          ...prev,
          procesosAuditados: prev.procesosAuditados.filter(
            (p) => p.id !== procesoAuditadoId,
          ),
        }));
        setEliminandoProceso(null);
        toast.current.show({
          severity: "success",
          summary: "Proceso eliminado",
          detail: "El proceso fue removido de la auditoría",
          life: 3000,
        });
      },
      (respuesta) => {
        setEliminandoProceso(null);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar proceso",
          life: 3000,
        });
      },
    );
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      {loader && (
        <div>
          {/* Skeleton del título */}
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1.5em",
              marginTop: "2em",
            }}
          >
            <Skeleton width="18em" height="2.2em" />
            <Skeleton width="10em" height="2.2em" />
          </div>

          {/* Skeleton de la card de información */}
          <div className="grid">
            <div className="col-12 lg:col-6">
              <Card>
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <Skeleton
                      width="6em"
                      height="1em"
                      css={{ marginBottom: "0.5em" }}
                    />
                    <Skeleton width="8em" height="1.2em" />
                  </div>
                  <div className="col-12 md:col-6">
                    <Skeleton
                      width="6em"
                      height="1em"
                      css={{ marginBottom: "0.5em" }}
                    />
                    <Skeleton
                      width="8em"
                      height="1.2em"
                      css={{ marginBottom: "1em" }}
                    />
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Skeleton de procesos asignados */}
          <Skeleton
            width="12rem"
            height="1.5rem"
            css={{ marginTop: "1.5em", marginBottom: "0.5em" }}
          />
          <div className="grid">
            {[1, 2, 3].map((i) => (
              <div className="col-12 md:col-6 lg:col-4" key={i}>
                <Card css={{ textAlign: "center", minHeight: "150px" }}>
                  <Skeleton
                    width="70%"
                    height="1.2rem"
                    css={{ margin: "0 auto 0.5rem auto" }}
                  />
                  <Skeleton
                    width="50%"
                    height="1rem"
                    css={{ margin: "0 auto 0.5rem auto" }}
                  />
                  <Skeleton
                    width="60%"
                    height="1rem"
                    css={{ margin: "0 auto 1rem auto" }}
                  />
                  <Skeleton
                    width="5rem"
                    height="2rem"
                    css={{ margin: "0 auto" }}
                  />
                </Card>
              </div>
            ))}
          </div>
        </div>
      )}
      {!loader && auditoria && (
        <div css={{ position: "relative" }}>
          <Sidebar
            visible={sidebarVisible}
            onHide={() => setSidebarVisible(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            <h2>Asignar procesos</h2>
            {loadingProcesos && (
              <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                {[1, 2, 3].map((i) => (
                  <div key={i}>
                    <Skeleton width="70%" height="1.5rem" css={{ marginBottom: "0.5rem" }} />
                    <Skeleton width="100%" height="3rem" />
                  </div>
                ))}
              </div>
            )}
            {!loadingProcesos && procesosDisponibles.length === 0 && (
              <p css={{ color: "#999" }}>
                No hay procesos disponibles para asignar
              </p>
            )}
            {!loadingProcesos && procesosDisponibles.length > 0 && (
              <Accordion
                multiple
                transitionOptions={{ timeout: 0 }}
                css={{
                  "& .p-accordion-header-link": { color: "black !important" },
                }}
              >
                {procesosDisponibles.map((grupo) => (
                  <AccordionTab
                    key={grupo.certificadoTipo.id}
                    header={grupo.certificadoTipo.nombre}
                  >
                    <div css={{ marginBottom: "0.5em" }}>
                      <Boton
                        transparente
                        callback={() => seleccionarAlAzar(grupo)}
                        disabled={agregandoProceso !== null}
                      >
                        <i className="pi pi-plus"></i> Aleatorio
                      </Boton>
                    </div>
                    {grupo.procesos.map((proceso) => (
                      <div
                        key={proceso.id}
                        css={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "0.5em",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        <div>
                          <strong>
                            {proceso.nombre}
                          </strong>
                          <br />
                          <span css={{ fontSize: "0.9em", color: "#666" }}>
                            {proceso.cliente.razonsocial}
                          </span>
                          <br />
                          <span css={{ fontSize: "0.85em", color: "#999" }}>
                            {proceso.nombrefantasia}
                          </span>
                        </div>
                        <Boton
                          icono
                          chico
                          negro
                          callback={() => agregarProceso(proceso.id)}
                          loader={agregandoProceso === proceso.id}
                          disabled={agregandoProceso !== null}
                        >
                          <i className="pi pi-plus"></i>
                        </Boton>
                      </div>
                    ))}
                  </AccordionTab>
                ))}
              </Accordion>
            )}
          </Sidebar>

          <Titulo
            urlVolver="/"
            botones={
              <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Boton
                  transparente
                  callback={() =>
                    navigate(`/auditoriasg/${auditoriaId}/cumplimiento`)
                  }
                >
                  Cumplimiento
                </Boton>
                <Boton callback={abrirSidebarProcesos}>Asignar procesos</Boton>
              </div>
            }
          >
            {auditoria.nombre}
          </Titulo>

          <div className="grid">
            <div className="col-12 lg:col-6">
              <Card>
                <div className="grid">
                  <CampoInformacion nombreCampo="Fecha de inicio">
                    {formatFecha(auditoria.fechaInicio)}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Fecha de fin">
                    {formatFecha(auditoria.fechaFin)}
                  </CampoInformacion>
                </div>
              </Card>
            </div>
          </div>

          {/* Procesos asignados */}
          <h3 css={{ marginTop: "1.5em", marginBottom: "0.5em" }}>
            Procesos asignados
          </h3>
          {auditoria.procesosAuditados.length === 0 && (
            <p css={{ color: "#999" }}>
              No hay procesos asignados a esta auditoría
            </p>
          )}
          <div className="grid">
            {auditoria.procesosAuditados.map((procesoAuditado) => (
              <div
                className="col-12 md:col-6 lg:col-4"
                key={procesoAuditado.id}
              >
                <Card
                  css={{
                    textAlign: "center",
                    height: "100%",
                    cursor: "pointer",
                    position: "relative",
                    "&:hover": { boxShadow: "0 4px 8px rgba(0,0,0,0.1)" },
                  }}
                  onClick={(event) =>
                    window.open(
                      `/auditoriasg/${auditoriaId}/proceso/${procesoAuditado.id}`,
                      event.metaKey || event.ctrlKey ? "_blank" : "_self",
                    )
                  }
                >
                  <div
                    css={{
                      position: "absolute",
                      top: "1em",
                      right: "1em",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Menu
                      model={[
                        {
                          label: "Eliminar",
                          icon: "pi pi-trash",
                          command: () => eliminarProceso(procesoAuditado.id),
                        },
                      ]}
                      popup
                      ref={(el) => (menuRefs.current[procesoAuditado.id] = el)}
                      id={`menu_proceso_${procesoAuditado.id}`}
                    />
                    <Boton
                      icono
                      chico
                      negro
                      transparente
                      callback={(event) =>
                        menuRefs.current[procesoAuditado.id]?.toggle(event)
                      }
                      loader={eliminandoProceso === procesoAuditado.id}
                    >
                      <i className="pi pi-ellipsis-v"></i>
                    </Boton>
                  </div>
                  <div>
                    <strong css={{ fontSize: "1.1em" }}>
                      {procesoAuditado.proceso.cliente.razonsocial}
                    </strong>
                    <p css={{ margin: "0.5em 0" }}>
                      <strong>{procesoAuditado.proceso.nombre}</strong>
                      {procesoAuditado.proceso.nombrefantasia && (
                        <span css={{ color: "#666" }}>
                          {" "}
                          - {procesoAuditado.proceso.nombrefantasia}
                        </span>
                      )}
                    </p>
                    <p css={{ margin: "0.5em 0", color: "#666" }}>
                      {procesoAuditado.proceso.certificadotipo?.codigo} -{" "}
                      {procesoAuditado.proceso.certificadotipo?.nombre}
                    </p>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
